import { useState } from "react";
import { useDispatch } from "react-redux";

import { Pill, money } from "../atoms";
import {
  rtmCounter,
  rtmDecline,
  rtmMatch,
  rtmPass,
  rtmUse,
} from "../../store/features/bidderSlice";

import "./index.scss";

// The Right To Match round, from the point of view of one team.
//
// Four decisions, alternating between two teams:
//
//   offered  the former team uses its card, or declines
//   counter  the top bidder makes one final raise, or stands pat
//   match    the former team matches that raise, or passes
//   settled  done
//
// Only the team being waited on gets buttons. Everyone else sees what is
// happening and who it is waiting on, because a blank panel during someone
// else's turn reads as a broken screen.
const RtmPanel = ({ player, franchise, purse, onActed }) => {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [raise, setRaise] = useState("");

  const rtm = player?.rtm;
  if (!rtm || rtm.stage === "none") return null;

  const same = (a, b) => a && b && String(a) === String(b);
  const myId = franchise?._id;
  const isHolder = same(rtm.holder, myId);
  const isChallenger = same(rtm.challenger, myId);
  const cards = franchise?.rtm ?? 0;

  const run = async (action, arg) => {
    setBusy(action.typePrefix || "run");
    setError("");
    const res = await dispatch(arg === undefined ? action() : action(arg));
    setBusy("");

    if (res?.payload?.statusCode === 200) {
      onActed?.(res.payload.data);
    } else {
      setError(res?.payload?.msg || "That did not go through. Try again.");
    }
  };

  const header = (label, note) => (
    <>
      <span className="au-label">{label}</span>
      {note && <p className="rtm-note">{note}</p>}
    </>
  );

  const waiting = (who, note) => (
    <div className="rtm-panel">
      {header("Right to match", note)}
      <Pill tone="quiet">Waiting on {who}</Pill>
    </div>
  );

  let body = null;

  if (rtm.stage === "settled") {
    const outcome = {
      matched: "Matched by the former team.",
      passed: "Former team passed. The top bid stands.",
      declined: "Right to match declined. The top bid stands.",
      skipped: "No right to match on this player.",
    }[rtm.outcome];

    body = (
      <div className="rtm-panel">
        {header("Right to match", outcome)}
        <Pill tone="success">Settled at {money(rtm.finalBid)}</Pill>
      </div>
    );
  } else if (rtm.stage === "offered") {
    if (!isHolder) return waiting("the former team", "They may match the top bid.");

    body = (
      <div className="rtm-panel">
        {header(
          "You can match this bid",
          cards > 0
            ? `${money(rtm.baseBid)} on the table. ${cards} card${cards === 1 ? "" : "s"} left.`
            : "You have no cards left."
        )}
        <div className="rtm-actions">
          <button
            type="button"
            className="au-btn success"
            disabled={Boolean(busy) || cards < 1}
            onClick={() => run(rtmUse)}
          >
            Use a card
          </button>
          <button
            type="button"
            className="au-btn quiet"
            disabled={Boolean(busy)}
            onClick={() => run(rtmDecline)}
          >
            Decline
          </button>
        </div>
      </div>
    );
  } else if (rtm.stage === "counter") {
    if (!isChallenger) {
      return waiting("the top bidder", "They get one final raise.");
    }

    const base = rtm.baseBid;
    const entered = raise !== "";
    const typed = Number(raise);
    // A final bid has to beat the price already on the table. Matching it is
    // not a bid, and that is what "not raising" already means.
    const tooLow = entered && Number.isFinite(typed) && typed <= base;
    const tooMuch =
      entered && Number.isFinite(typed) && purse != null && typed > purse;
    const canBid = entered && Number.isFinite(typed) && !tooLow && !tooMuch;

    body = (
      <div className="rtm-panel">
        {header(
          "One final bid",
          `The former team used a card. Bid above ${money(base)} to hold on to ${player?.name || "this player"}, or let them have the player at ${money(base)}.`
        )}
        <div className="rtm-raise">
          <input
            type="number"
            className="rtm-input"
            min={base + 1}
            step={1}
            value={raise}
            placeholder={`More than ${base}`}
            onChange={(e) => setRaise(e.target.value)}
          />
          <span className="rtm-unit">L</span>
        </div>
        {(tooLow || tooMuch) && (
          <p className="rtm-error">
            {tooLow
              ? `Must be above ${money(base)}.`
              : `More than your purse of ${money(purse)}.`}
          </p>
        )}
        <div className="rtm-actions">
          <button
            type="button"
            className="au-btn primary"
            disabled={Boolean(busy) || !canBid}
            onClick={() => run(rtmCounter, typed)}
          >
            {canBid ? `Bid ${money(typed)}` : "Enter a higher bid"}
          </button>
          <button
            type="button"
            className="au-btn quiet"
            disabled={Boolean(busy)}
            onClick={() => run(rtmCounter)}
          >
            Do not raise
          </button>
        </div>
      </div>
    );
  } else if (rtm.stage === "match") {
    if (!isHolder) {
      return waiting(
        "the former team",
        `They must match ${money(rtm.finalBid)} to take the player.`
      );
    }

    const cannotAfford = purse != null && rtm.finalBid > purse;

    body = (
      <div className="rtm-panel">
        {header(
          "Match the final bid",
          cannotAfford
            ? `${money(rtm.finalBid)} is more than your purse of ${money(purse)}.`
            : `The top bidder went to ${money(rtm.finalBid)}. Match it to take the player.`
        )}
        <div className="rtm-actions">
          <button
            type="button"
            className="au-btn success"
            disabled={Boolean(busy) || cannotAfford || cards < 1}
            onClick={() => run(rtmMatch)}
          >
            Match {money(rtm.finalBid)}
          </button>
          <button
            type="button"
            className="au-btn quiet"
            disabled={Boolean(busy)}
            onClick={() => run(rtmPass)}
          >
            Pass
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {body}
      {error && <p className="rtm-error">{error}</p>}
    </>
  );
};

export default RtmPanel;
